import { FC, useContext, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';
import { deepmerge } from 'deepmerge-ts';
import { SwitchThemeContext, theme, useViewport } from '@tryhackme/thm-ui-components';
import { QuestionTask } from 'src/common/interfaces/tasks';
import { ThemeColorMode } from 'src/common/enums';
import { ChartProps } from './chart.types';
import { darkThemeOverride, getAspectRatio, getUserData, lightThemeOverride, extendedLineColors, options } from './chart.constants';
import { StyledChartWrapper, StyledInfoBox, StyledChartSkeleton } from './chart.styles';
export const Chart: FC<ChartProps> = ({
  chartDetails,
  isLoading
}) => {
  const {
    theme: themeName
  } = useContext(SwitchThemeContext);
  const {
    width
  } = useViewport();
  const xAxis = useMemo(() => {
    const mappedLabels = (chartDetails?.reduce((total, scoreRow) => {
      const dateLabels = Object.values(scoreRow.tasks).flat<Omit<QuestionTask, 'submission'>[]>().map(progress => progress.timeCorrect);
      return [...total, ...dateLabels];
    }, ([] as QuestionTask['timeCorrect'][])) ?? []).filter(Boolean).sort((a, b) => {
      if (a && b) {
        return new Date(a).getTime() - new Date(b).getTime();
      }
      return -1;
    });
    // Adding 5 more x axis ticks
    mappedLabels.splice(mappedLabels.length, 0, ...Array.from({
      length: 5
    }, (_, index) => new Date(+Date.now() + index * 1000 * 60 * 60 * 24).toISOString()));
    return mappedLabels;
  }, [chartDetails]);
  const yAxis: ChartData<'line'> = useMemo(() => ({
    labels: xAxis,
    datasets: chartDetails?.map((chartDetail, index) => ({
      label: chartDetail.username,
      data: getUserData(xAxis, chartDetail),
      borderColor: extendedLineColors[index]?.borderColor,
      backgroundColor: extendedLineColors[index]?.backgroundColor,
      cubicInterpolationMode: 'monotone',
      tension: 0.4,
      fill: false
    })) ?? []
  } satisfies ChartData<'line'>), [chartDetails, xAxis]);
  const overrideOptions = useMemo(() => {
    const isDarkMode = themeName === ThemeColorMode.DARK;
    const overrideAll = ({
      aspectRatio: getAspectRatio(width, theme.sizes)
    } satisfies ChartOptions<'line'>);
    return deepmerge(options, isDarkMode ? darkThemeOverride : lightThemeOverride, overrideAll);
  }, [themeName, width]);
  const noData = useMemo(() => {
    if (!chartDetails && !isLoading) return true;
    if (chartDetails?.every(chartRow => !chartRow.tasks || Object.keys(chartRow.tasks).length === 0)) return true;
    return false;
  }, [chartDetails, isLoading]);
  if (noData) return <StyledInfoBox>No chart data to display.</StyledInfoBox>;
  if (isLoading) return <StyledChartSkeleton />;
  return <StyledChartWrapper data-testid="chartWrapper" data-sentry-element="StyledChartWrapper" data-sentry-component="Chart" data-sentry-source-file="chart.tsx">
      <Line options={overrideOptions} data={yAxis} data-sentry-element="Line" data-sentry-source-file="chart.tsx" />
    </StyledChartWrapper>;
};